import {objectFromEntries} from '@benchling/js-utils';
import Backbone from 'backbone';

import * as analytics from 'analytics/Analytics';
import PreviewModalView from 'components/PreviewModalView';
import {Sequence} from 'components/Sequence';
import SearchDNAData from 'molbio/biological-search/SearchDNAData';
import {SinglePolymerMapState} from 'molbio/maps/MapStates';
import Pagination from 'molbio/pdf/Pagination';
import SinglePolymerMapTabState from 'molbio/tabstates/SinglePolymerMapTabState';
import {
  FeatureCollectionName,
  FeatureCounts,
  VisibilityOptions,
} from 'settings/visibility-options/VisibilityOptions';
import {toFilename} from 'util/FileUtil';

export interface SequenceOptionsBaseProps {
  bannerText: React.ReactNode;
  featureCounts: FeatureCounts;
  onOpenPreview: () => void;
  onToggleSearch: () => void;
  sequenceId: string;
  visibilityOptions: VisibilityOptions;
}

abstract class SequenceOptionsView<
  MapState extends SinglePolymerMapState<Sequence>,
  SequenceMapTabState extends SinglePolymerMapTabState<Sequence, MapState>,
> extends Backbone.View<SequenceMapTabState> {
  mapstate!: MapState;

  abstract previewSourceName: string;
  abstract previewPaginate: (
    tabState: SequenceMapTabState,
    mapState: MapState,
    css: string | null
  ) => Pagination;
  abstract handleBigSequence(): void;
  abstract trackedFeatureCollections(): Array<FeatureCollectionName>;

  static initClass(): void {
    this.prototype.tagName = 'div';
    this.prototype.className = 'reset';
  }

  override initialize(options?: Backbone.ViewOptions<SequenceMapTabState>): void {
    this.mapstate = this.model.get('mapstate')!;
    const sequence = this.mapstate.sequence();
    this.handleBigSequence();
    this.listenTo(sequence, 'change:banner', () => {
      this.render();
    });
    this.listenTo(sequence._sequence(), 'change:bases', () => {
      this.handleBigSequence();
    });
    for (const featureCollectionName of this.trackedFeatureCollections()) {
      if (featureCollectionName === 'parts') {
        const partsCollection = sequence.parts();
        // Special cases for parts:
        // in some cases we remove and re-add the entire part collection
        this.listenTo(sequence._sequence(), 'change:parts', () => this.render());
        // Parts do not support batch updates, so skip listenToBatch callback wrapper
        this.listenTo(partsCollection, 'add remove reset change', () => this.render());
      } else if (featureCollectionName === 'transcriptions') {
        this.listenTo(sequence, 'change:transcriptionMatches', () => this.render());
      } else {
        const collection = sequence[featureCollectionName]();
        const setCount = this.listenToBatch<Backbone.Model>(collection, () => this.render());
        this.listenTo(collection, 'add remove reset', () => {
          setCount();
        });
      }
    }
  }

  getFeatureCounts(): FeatureCounts {
    const sequence = this.mapstate.sequence();
    return objectFromEntries(
      this.trackedFeatureCollections().map((featureCollectionName) => {
        if (featureCollectionName === 'transcriptions') {
          return [featureCollectionName, sequence.get('transcriptionMatches').length];
        }
        return [featureCollectionName, sequence[featureCollectionName]().getCount()];
      })
    );
  }

  getProps(): SequenceOptionsBaseProps {
    const sequence = this.mapstate.sequence();
    return {
      bannerText: sequence.getBannerText(),
      featureCounts: this.getFeatureCounts(),
      sequenceId: sequence.id,
      visibilityOptions: this.model.get('visibilityOptions')!,
      onToggleSearch: () => {
        const searchDNAData = SearchDNAData.get();
        const isSearchActive = searchDNAData.get('mode') != null;
        searchDNAData.trigger(isSearchActive ? 'close' : 'open');
      },
      onOpenPreview: () => {
        const trackMetadata = {
          source: this.previewSourceName,
          sequence_api_id: sequence.get('_sequence').id,
          sequence_length: sequence.length(),
        };

        const modalView = new PreviewModalView({
          paginate: (css: string | null) => this.previewPaginate(this.model, this.mapstate, css),
          landscape: false,
          trackMetadata,
          filename: toFilename(`${sequence.name()}-sequence`, 'pdf'),
        });
        modalView.render();
        analytics.track('editor', 'opened_pdf_preview', trackMetadata);
      },
    };
  }
}
SequenceOptionsView.initClass();

export default SequenceOptionsView;
